// JournalPage.js — Сторінка "Зведений опис журналу"
//
// Показує зведену бібліографічну картку журналу (періодичного видання) та
// список років реєстрації надходжень (поле 909, journal.php?action=summary).
// Клік по року підвантажує список конкретних номерів цього року
// (journal.php?action=issues) — за зразком drill-down навігації UdcPage.js.

import React, { useState, useEffect, useCallback } from 'react';
import { API_URL } from '../config';
import { t } from '../i18n';
import PaginationBlock from './PaginationBlock';
import CoverArt from './CoverArt';
import { safeExtUrl as _safeExtUrl } from '../safeUrl';
import './JournalPage.css';

const JOURNAL_STATE_KEY = 'irbis_journal_state';
// Пагінація років/номерів (сесія 2026-08-06) — журнал з довгою історією може
// мати 60-100+ карток років, а рік випуску-щотижневика — 40-50+ номерів;
// суцільний список без пагінації перевантажує інтерфейс. Поріг для номерів
// навмисно високий — у більшості журналів ≤12 номерів/рік, пагінація там
// узагалі не з'явиться.
const YEARS_PER_PAGE  = 10;
const ISSUES_PER_PAGE = 24;

function readJournalState() {
  try { return JSON.parse(sessionStorage.getItem(JOURNAL_STATE_KEY) || '{}'); }
  catch { return {}; }
}

export default function JournalPage({ db, code, initialYear, lang, onOpenBook, onYearOpen, onBack, backLabel }) {
  const savedState = readJournalState();

  const [summary,      setSummary]      = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState('');

  const [selectedYear, setSelectedYear] = useState(() =>
    initialYear || (savedState.code === code ? savedState.year || null : null)
  );
  const [issues,        setIssues]        = useState(null);
  const [issuesLoading, setIssuesLoading] = useState(false);
  const [issuesError,   setIssuesError]   = useState('');

  const [yearsPage,  setYearsPage]  = useState(1);
  const [issuesPage, setIssuesPage] = useState(1);

  // ── Завантаження зведеної картки ──────────────────────────
  useEffect(() => {
    setLoading(true);
    setError('');
    setSummary(null);
    setYearsPage(1);
    fetch(`${API_URL}/journal.php?action=summary&db=${encodeURIComponent(db)}&code=${encodeURIComponent(code)}`)
      .then(r => r.json())
      .then(data => {
        if (data.error) { setError(data.error); return; }
        setSummary(data);
      })
      .catch(() => setError(t(lang, 'journal_not_found')))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [db, code]);

  // ── Завантаження номерів обраного року ────────────────────
  const loadIssues = useCallback((year) => {
    setIssuesLoading(true);
    setIssuesError('');
    setIssues(null);
    setIssuesPage(1);
    fetch(`${API_URL}/journal.php?action=issues&db=${encodeURIComponent(db)}&code=${encodeURIComponent(code)}&year=${encodeURIComponent(year)}`)
      .then(r => r.json())
      .then(data => setIssues(data.issues || []))
      .catch(() => setIssuesError(t(lang, 'journal_issues_error')))
      .finally(() => setIssuesLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [db, code, lang]);

  useEffect(() => {
    if (selectedYear) loadIssues(selectedYear);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear, loadIssues]);

  // ── Збереження стану для відновлення після перезавантаження ──
  useEffect(() => {
    try {
      sessionStorage.setItem(JOURNAL_STATE_KEY, JSON.stringify({ code, year: selectedYear }));
    } catch {}
  }, [code, selectedYear]);

const handleYearClick = (year) => {
    setSelectedYear(prev => {
      const next = prev === year ? null : year;
      if (next !== null && onYearOpen) onYearOpen(next);
      return next;
    });
  };

  const handleYearsPageChange = (p) => {
    setSelectedYear(null);
    setYearsPage(p);
  };

  const selectedYearData = summary?.years?.find(y => y.year === selectedYear) || null;

  const yearsTotalPages = summary ? Math.max(1, Math.ceil(summary.years.length / YEARS_PER_PAGE)) : 1;
  const pagedYears = summary
    ? summary.years.slice((yearsPage - 1) * YEARS_PER_PAGE, yearsPage * YEARS_PER_PAGE)
    : [];

  const issuesTotalPages = issues ? Math.max(1, Math.ceil(issues.length / ISSUES_PER_PAGE)) : 1;
  const pagedIssues = issues
    ? issues.slice((issuesPage - 1) * ISSUES_PER_PAGE, issuesPage * ISSUES_PER_PAGE)
    : [];

  return (
    <div className="journal-page">
      <div className="journal-container">
        <button className="journal-back-btn" onClick={onBack}>
          <i className="fa-solid fa-arrow-left" />
          {backLabel}
        </button>

        {loading ? (
          <div className="journal-skeleton">
            <div className="journal-skeleton-title" />
            <div className="journal-skeleton-line" style={{ width: '60%' }} />
            <div className="journal-skeleton-line" style={{ width: '40%' }} />
          </div>
        ) : error ? (
          <div className="journal-error">{error}</div>
        ) : summary && (
          <>
            {/* ── Зведений бібліографічний опис ── */}
            <div className="journal-summary-card">
              <div className="journal-header">
                <i className="fa-solid fa-book-open-reader journal-header-icon" />
                <h2 className="journal-title">
                  {summary.title}
                  {summary.subtitle && (
                    <span className="journal-title-subtitle"> : {summary.subtitle}</span>
                  )}
                </h2>
              </div>
              {summary.parallel_title && (
                <p className="journal-parallel-title">{summary.parallel_title}</p>
              )}
              {summary.responsibility && (
                <p className="journal-responsibility">{summary.responsibility}</p>
              )}
              <div className="journal-meta-row">
                {(summary.place || summary.publisher) && (
                  <span>
                    {summary.place}{summary.place ? ' : ' : ''}{summary.publisher || '[б. в.]'}
                    {summary.year_founded ? `, ${summary.year_founded}` : ''}
                    {summary.printing_place ? ` (${summary.printing_place})` : ''}
                  </span>
                )}
                {summary.issn && <span>ISSN {summary.issn}</span>}
              </div>
              {summary.editor && (
                <p className="journal-editor">
                  {summary.editor_role ? `${summary.editor_role}: ` : ''}{summary.editor}
                </p>
              )}
              {summary.rubrics?.length > 0 && (
                <p className="journal-rubrics">{summary.rubrics.join('; ')}</p>
              )}
              {(summary.external_links?.length > 0 || summary.doi) && (
                <div className="journal-links">
                  {summary.external_links.map((l, i) => (
                    <a key={i} href={_safeExtUrl(l.url)} target="_blank" rel="noopener noreferrer" className="journal-link">
                      <i className="fa-solid fa-arrow-up-right-from-square me-1" />
                      {l.label?.trim() || l.url}
                    </a>
                  ))}
                  {summary.doi && (
                    <a href={_safeExtUrl(summary.doi)} target="_blank" rel="noopener noreferrer" className="journal-link journal-link--doi">
                      <i className="fa-solid fa-fingerprint me-1" />
                      DOI
                    </a>
                  )}
                </div>
              )}
            </div>

            {/* ── Роки надходження (поле 909) ── */}
            <div className="journal-years-block">
              <h3 className="journal-years-title">
                <i className="fa-solid fa-calendar-days me-2" />
                {t(lang, 'journal_years_title')}
              </h3>
<div className="journal-years-row">
                {pagedYears.map(y => (
                  <button
                    key={y.year}
                    className={`journal-year-pill${selectedYear === y.year ? ' journal-year-pill--active' : ''}`}
                    onClick={() => handleYearClick(y.year)}
                  >
                    <i className="fa-solid fa-calendar-day journal-year-pill-icon" />
                    <span className="journal-year-pill-year">{y.year}</span>
                    <span className="journal-year-pill-sub">
                      {y.batches.length === 1 ? (
                        <>
                          {y.batches[0].kit ? `компл.${y.batches[0].kit}` : ''}
                          {y.online && <i className="fa-solid fa-wifi journal-year-pill-online" title={t(lang, 'journal_online')} />}
                          {y.batches[0].range ? ` N: ${y.batches[0].range}` : ''}
                        </>
                      ) : (
                        <>
                          {t(lang, 'journal_batches_label')}: {y.batches.length}
                          {y.online && <i className="fa-solid fa-wifi journal-year-pill-online" title={t(lang, 'journal_online')} />}
                        </>
                      )}
                    </span>
                  </button>
                ))}
              </div>
              {yearsTotalPages > 1 && (
                <PaginationBlock
                  page={yearsPage}
                  totalPages={yearsTotalPages}
                  onGo={handleYearsPageChange}
                  className="journal-years-pagination"
                  lang={lang}
                />
              )}
            </div>

            {/* ── Номери обраного року ── */}
{!selectedYear ? (
              <div className="journal-select-year-hint">
                <i className="fa-solid fa-hand-pointer journal-select-year-hint-icon" />
                {t(lang, 'journal_select_year_hint')}
              </div>
            ) : (
              <div className="journal-issues-block">
                {selectedYearData?.batches?.length > 1 && (
                  <div className="journal-batches-info">
                    {selectedYearData.batches.map((b, i) => (
                      <span key={i} className="journal-batch-chip">
                        {b.kit ? `компл.${b.kit}` : ''}
                        {b.range ? ` N: ${b.range}` : ''}
                        {b.online && (
                          <i className="fa-solid fa-wifi journal-batch-chip-online" title={t(lang, 'journal_online')} />
                        )}
                      </span>
                    ))}
                  </div>
                )}
                {issuesLoading || (!issues && !issuesError) ? (
                  <div className="journal-skeleton">
                    <div className="journal-skeleton-line" style={{ width: '80%' }} />
                    <div className="journal-skeleton-line" style={{ width: '70%' }} />
                  </div>
                ) : issuesError ? (
                  <div className="journal-error">{issuesError}</div>
                ) : issues.length === 0 ? (
                  <div className="journal-issues-empty">{t(lang, 'journal_issues_empty')}</div>
                ) : (
                  <div className="journal-issues-list">
                    {pagedIssues.map(iss => (
                      <button
                        key={iss.mfn}
                        className="journal-issue-card"
                        onClick={() => onOpenBook(iss.mfn, iss.db, iss.title || summary?.title)}
                      >
                        {iss.cover_url ? (
                          <img src={iss.cover_url} alt="" className="journal-issue-cover" />
                        ) : (
                          <div className="journal-issue-cover journal-issue-cover--placeholder cover-art-host">
                            <CoverArt kind="journal" />
                          </div>
                        )}
                        <div className="journal-issue-body">
                          <span className="journal-issue-label">
                            {selectedYear}, № {iss.issue_label}
                          </span>
                          {/* Записи-номери періодики зазвичай не мають власного
                              200^a (сесія 2026-08-07/2) — показуємо назву журналу
                              зі summary, вже завантаженого на цій сторінці, замість
                              порожнього/зайвого дубля */}
                          <span className="journal-issue-title">{iss.title || summary?.title}</span>
                          {iss.contents_count > 0 && (
                            <span className="journal-issue-contents-badge">
                              <i className="fa-solid fa-list-ul me-1" />
                              {t(lang, 'journal_contents_badge')}: {iss.contents_count}
                            </span>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                {issuesTotalPages > 1 && (
                  <PaginationBlock
                    page={issuesPage}
                    totalPages={issuesTotalPages}
                    onGo={setIssuesPage}
                    className="journal-issues-pagination"
                    lang={lang}
                  />
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
